/**
 * Toast / ToastContainer — 글로벌 알림 토스트
 *
 * useUIStore의 toasts 배열을 구독하여 화면 우상단에 렌더링한다.
 * 각 토스트는 타입별 색상 테두리와 닫기 버튼을 가진다.
 */

import { Link } from 'react-router'
import { useUIStore } from '../../stores/uiStore.js'
import { cn } from '../../lib/cn.js'
import styles from './Toast.module.css'
import type { Toast } from '../../stores/uiStore.js'

/** 타입별 접근성 레이블 */
const TYPE_LABEL: Record<Toast['type'], string> = {
  success: '성공',
  error: '오류',
  info: '안내',
}

interface ToastItemProps {
  toast: Toast
  onClose: (id: string) => void
}

function ToastItem({ toast, onClose }: ToastItemProps) {
  return (
    <div
      role="alert"
      aria-live="assertive"
      className={cn(styles.toast, styles[toast.type])}
    >
      {/* 타입 표시 점 */}
      <span className={styles.dot} aria-label={TYPE_LABEL[toast.type]} />
      <span className={styles.message}>
        {toast.message}
        {toast.action && (
          <Link to={toast.action.href} className={styles.action}>
            {toast.action.label}
          </Link>
        )}
      </span>
      <button
        className={styles.closeBtn}
        onClick={() => onClose(toast.id)}
        aria-label="토스트 닫기"
      >
        ✕
      </button>
    </div>
  )
}

/** 앱 최상단에 한 번만 마운트하는 토스트 컨테이너 */
export function ToastContainer() {
  const toasts = useUIStore((s) => s.toasts)
  const removeToast = useUIStore((s) => s.removeToast)

  if (toasts.length === 0) return null

  return (
    <div className={styles.container} aria-label="알림 목록">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onClose={removeToast} />
      ))}
    </div>
  )
}
